// utils/media/mimeUtils.ts
//
// Unified MIME helpers for TownLIT
// - Single source of truth for allowed MIME/EXT
// - Safe recorder MIME pickers for MediaRecorder
// - Smart file normalization (stable MIME/extension)
// - Lightweight magic-bytes sniffing for sanity checks
//
// NOTE:
// * Keep validators importing the exported allowlist helpers:
//     isAllowedAudioMime / isAllowedAudioExt / isAllowedVideoMime / ...
// * Never hardcode MIME regexes elsewhere—extend here only.

export type StringMap = Record<string, string>;

/* ---------------------------------------------
   Capability helpers
--------------------------------------------- */

/** Strips parameters like ";codecs=opus" and lowercases. */
export const normalizeBaseMime = (mime?: string): string =>
  (mime || "").toLowerCase().split(";")[0].trim();

/** Feature-detects MediaRecorder.isTypeSupported safely. */
export const isTypeSupported = (mime: string): boolean => {
  try {
    const MR: any = (globalThis as any).MediaRecorder;
    return typeof MR?.isTypeSupported === "function" ? MR.isTypeSupported(mime) : false;
  } catch {
    return false;
  }
};

/** Minimal Safari hint (used as a tie-breaker only). */
export const isLikelySafari = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const vendor = (navigator as any).vendor || "";
  return /Safari/i.test(ua) && /Apple/i.test(vendor) && !/Chrome/i.test(ua);
};

/* ---------------------------------------------
   Extension → MIME map (single source of truth)
--------------------------------------------- */

const extensionMimeMap: StringMap = {
  // Images
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  heic: "image/heic",

  // Videos
  mp4: "video/mp4",
  m4v: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",        // ← keep webm once (defaults to video)
  ogv: "video/ogg",
  mkv: "video/x-matroska",
  avi: "video/x-msvideo",
  "3gp": "video/3gpp",
  "3g2": "video/3gpp2",

  // Audios
  mp3: "audio/mpeg",
  wav: "audio/wav",
  m4a: "audio/mp4",
  aac: "audio/aac",
  mp4a: "audio/mp4",
  oga: "audio/ogg",
  ogg: "audio/ogg",
  webma: "audio/webm",       // ← audio-WebM (alias used by some tools)
  weba: "audio/webm",        // ← add common alias for audio-only WebM
  flac: "audio/flac",
  opus: "audio/ogg",         // Opus-in-Ogg commonly uses .opus

  // Files
  pdf: "application/pdf",
  zip: "application/zip",
  txt: "text/plain",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  md: "text/markdown",
};

/* ---------------------------------------------
   Recorder MIME pickers (MediaRecorder)
--------------------------------------------- */

/** Prefer webm/opus, then ogg/opus (FF), then mp4/aac (Safari). */
export const pickBestAudioRecorderMime = (): string | undefined => {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
  ];
  for (const t of candidates) if (isTypeSupported(t)) return t;
  return undefined; // let the browser pick
};

/** VP8/VP9 for Chromium/Firefox; H264/AAC MP4 for Safari. */
export const pickBestVideoRecorderMime = (): string | undefined => {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4;codecs=h264,aac",
    "video/mp4",
  ];
  for (const t of candidates) if (isTypeSupported(t)) return t;
  return undefined;
};

/* ---------------------------------------------
   Smart MIME choices when no extension exists
--------------------------------------------- */

const pickSmartAudioMimeForFiles = (): string => {
  if (isTypeSupported("audio/webm;codecs=opus") || isTypeSupported("audio/webm")) return "audio/webm";
  // Safari path (no webm): AAC/MP4 is the safest fallback
  return "audio/mp4";
};

const pickSmartVideoMimeForFiles = (): string => {
  if (isTypeSupported("video/webm;codecs=vp8,opus") || isTypeSupported("video/webm")) return "video/webm";
  return "video/mp4"; // Safari prefers h264 in mp4/quicktime
};

/** Resolve a reasonable MIME from a media-type hint and/or filename. */
export const getMimeTypeFromMediaType = (mediaType: "image" | "video" | "audio" | "file", fileName?: string): string => {
  const ext = fileName?.split(".").pop()?.toLowerCase();
  if (ext && extensionMimeMap[ext]) return extensionMimeMap[ext];

  switch (mediaType) {
    case "image": return "image/jpeg";
    case "video": return pickSmartVideoMimeForFiles();
    case "audio": return pickSmartAudioMimeForFiles();
    default: return "application/octet-stream";
  }
};

/* ---------------------------------------------
   MIME ↔ extension helpers
--------------------------------------------- */

/** Best-effort mapping from MIME to extension (codecs stripped). */
export const getExtensionFromMimeType = (mimeType: string): string => {
  const m = normalizeBaseMime(mimeType);
  const table: StringMap = {
    // Audio
    "audio/mpeg": "mp3",
    "audio/ogg": "ogg",
    "audio/wav": "wav",
    "audio/webm": "webm",
    "audio/mp4": "m4a",
    "audio/aac": "aac",
    "audio/flac": "flac",

    // Video
    "video/webm": "webm",
    "video/ogg": "ogv",
    "video/mp4": "mp4",
    "video/quicktime": "mov",
    "video/x-matroska": "mkv",
    "video/x-msvideo": "avi",
    "video/3gpp": "3gp",
    "video/3gpp2": "3g2",

    // Images
    "image/jpeg": "jpg",
    "image/png": "png",
    "image/webp": "webp",
    "image/heic": "heic",

    // Docs
    "application/pdf": "pdf",
  };
  return table[m] || "bin";
};

/** Keep last path segment from a URL or return fallback. */
export const getCleanFileName = (urlOrName: string, fallback: string): string => {
  try {
    const parts = urlOrName.split("/");
    const last = parts[parts.length - 1];
    return last || fallback;
  } catch {
    return fallback;
  }
};

/* ---------------------------------------------
   Allowlist sets (export for validators)
--------------------------------------------- */

const AUDIO_EXT_WHITELIST = new Set<string>(
  Object.keys(extensionMimeMap).filter(ext => (extensionMimeMap[ext] || "").startsWith("audio/"))
);
// Normalized extras that users may upload/record
["mp3", "m4a", "aac", "wav", "ogg", "webm", "mp4", "flac", "opus"].forEach(e => AUDIO_EXT_WHITELIST.add(e));

const AUDIO_MIME_WHITELIST = new Set<string>(
  Object.values(extensionMimeMap)
    .filter(m => m.startsWith("audio/"))
    .map(normalizeBaseMime)
);
// Normalize common runtime variants
["audio/mp3", "audio/x-wav", "audio/x-m4a", "audio/webm", "audio/ogg", "audio/mpeg", "audio/mp4", "audio/aac", "audio/flac"]
  .forEach(m => AUDIO_MIME_WHITELIST.add(normalizeBaseMime(m)));

const VIDEO_EXT_WHITELIST = new Set<string>(
  Object.keys(extensionMimeMap).filter(ext => (extensionMimeMap[ext] || "").startsWith("video/"))
);
// Popular extras
["mp4", "webm", "ogg", "mov", "mkv", "avi", "m4v", "3gp", "3g2"].forEach(e => VIDEO_EXT_WHITELIST.add(e));

const VIDEO_MIME_WHITELIST = new Set<string>(
  Object.values(extensionMimeMap)
    .filter(m => m.startsWith("video/"))
    .map(normalizeBaseMime)
);
// Normalize common runtime variants
["video/webm", "video/ogg", "video/mp4", "video/quicktime", "video/x-matroska", "video/x-msvideo", "video/3gpp", "video/3gpp2"]
  .forEach(m => VIDEO_MIME_WHITELIST.add(normalizeBaseMime(m)));

const IMAGE_EXT_WHITELIST = new Set<string>(["png", "jpg", "jpeg", "webp", "gif", "heic"]);
const IMAGE_MIME_WHITELIST = new Set<string>(["image/png", "image/jpeg", "image/webp", "image/gif", "image/heic"].map(normalizeBaseMime));

export const isAllowedAudioMime = (mime: string) => AUDIO_MIME_WHITELIST.has(normalizeBaseMime(mime));
export const isAllowedAudioExt = (ext: string) => AUDIO_EXT_WHITELIST.has((ext || "").toLowerCase());

export const isAllowedVideoMime = (mime: string) => VIDEO_MIME_WHITELIST.has(normalizeBaseMime(mime));
export const isAllowedVideoExt = (ext: string) => VIDEO_EXT_WHITELIST.has((ext || "").toLowerCase());

export const isAllowedImageMime = (mime: string) => IMAGE_MIME_WHITELIST.has(normalizeBaseMime(mime));
export const isAllowedImageExt = (ext: string) => IMAGE_EXT_WHITELIST.has((ext || "").toLowerCase());

/* ---------------------------------------------
   Normalization utilities
--------------------------------------------- */

/**
 * Coerce a MIME to audio/* if we *know* the stream had no video track.
 * Useful when Firefox yields "video/webm" for audio-only recordings.
 */
export const coerceToAudioIfNoVideo = (mime: string, hasVideoTrack: boolean): string => {
  const base = normalizeBaseMime(mime);
  if (hasVideoTrack) return base;
  if (base === "video/webm") return "audio/webm";
  if (base === "video/ogg") return "audio/ogg";
  return base;
};

/**
 * Normalize a File for upload:
 * - Keep existing file.type if present, otherwise infer from mediaType/filename.
 * - Ensure filename extension matches the resolved MIME.
 * - Does NOT transcode; only wraps the bytes with coherent name/type.
 */
export function normalizeUploadFile(
  file: File,
  mediaType: "image" | "video" | "audio" | "file"
): File {
  const resolvedMime = file.type || getMimeTypeFromMediaType(mediaType, file.name);
  const ext = getExtensionFromMimeType(resolvedMime);

  const hasExt = /\.[a-z0-9]+$/i.test(file.name);
  const base = hasExt ? file.name.replace(/\.[a-z0-9]+$/i, "") : file.name;
  const name = ext === "bin" ? file.name : `${base}.${ext}`;

  return new File([file], name, { type: normalizeBaseMime(resolvedMime) });
}

/* ---------------------------------------------
   Magic-bytes sniffing (best-effort)
--------------------------------------------- */

/**
 * Best-effort MIME detection using magic bytes (NOT exhaustive).
 * Returns a base MIME without parameters, or null if unknown.
 */
export function detectMimeFromBytes(bytes: ArrayBuffer | Uint8Array): string | null {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  if (!view || view.length < 12) return null;

  const eq = (off: number, ascii: string) => {
    for (let i = 0; i < ascii.length; i++) if (view[off + i] !== ascii.charCodeAt(i)) return false;
    return true;
  };

  // WebM (EBML)
  if (view[0] === 0x1a && view[1] === 0x45 && view[2] === 0xdf && view[3] === 0xa3) {
    return "video/webm"; // container only; audio-only streams may still use webm
  }
  // MP4 / ISO-BMFF ("ftyp" at offset 4)
  if (view.length > 12 && eq(4, "ftyp")) return "video/mp4";
  // OGG
  if (eq(0, "OggS")) return "audio/ogg";
  // WAV (RIFF....WAVE)
  if (eq(0, "RIFF") && eq(8, "WAVE")) return "audio/wav";
  // MP3
  if (eq(0, "ID3") || (view[0] === 0xff && (view[1] & 0xe0) === 0xe0)) return "audio/mpeg";
  // Images
  if (view[0] === 0xff && view[1] === 0xd8 && view[2] === 0xff) return "image/jpeg";
  if (view[0] === 0x89 && eq(1, "PNG")) return "image/png";
  if (eq(0, "GIF87a") || eq(0, "GIF89a")) return "image/gif";
  if (eq(0, "RIFF") && eq(8, "WEBP")) return "image/webp";

  return null;
}
